import {
  EMPLOYMENT_LABELS,
  SENIORITY_LABELS,
  canCloseRole,
  fmtDate,
  label,
  regionShortLabel,
} from '@remote-medium/shared';
import {
  ApiRequestError,
  Badge,
  Button,
  ErrorState,
  LoadingState,
  RoleForm,
  buttonVariants,
  cn,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import type { ClientResponse } from 'hono/client';
import {
  ArrowUpRight,
  Banknote,
  Briefcase,
  Building2,
  CircleAlert,
  Clock,
  Globe,
  Layers,
  Pencil,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { Candidates } from './components/Candidates.tsx';
import { useBillingEnabled } from '../../lib/billing.tsx';
import { admin } from '../../lib/client.ts';
import { RoleStatusChip } from '../../lib/status-chip.tsx';
import { Card, ConfirmButton, None, Notice, Page, PageHeader } from '../../lib/ui.tsx';

/** "USD 10k–111k", "up to USD 90k", "from USD 40k". */
function salaryRange(min: number | null, max: number | null, currency: string | null) {
  if (!min && !max) return null;
  const k = (n: number) =>
    new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
      .format(n)
      .toLowerCase();
  const range = min && max ? `${k(min)}–${k(max)}` : min ? `from ${k(min)}` : `up to ${k(max!)}`;
  return currency ? `${currency} ${range}` : range;
}

function SkillChips({ names, outline }: { names: string[]; outline?: boolean }) {
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      {names.map((n) => (
        <span
          key={n}
          className={cn(
            'inline-flex h-6 items-center rounded-sm px-2 text-xs',
            outline ? 'border border-border' : 'bg-secondary text-secondary-foreground',
          )}
        >
          {n}
        </span>
      ))}
    </span>
  );
}

export function RoleDetailPage() {
  const billing = useBillingEnabled();
  const { id } = useParams();
  const { data, error, loading, setData } = useApi(
    () => unwrap(admin.roles[':id'].$get({ param: { id: id! } })),
    [id],
  );
  const { data: taxonomy } = useApi(() => unwrap(admin.taxonomy.$get()), []);
  // Only for the credit balance; the company name comes with the role.
  const { data: owner } = useApi(
    () =>
      data?.companyId
        ? unwrap(admin.companies[':id'].$get({ param: { id: data.companyId } }))
        : Promise.resolve(null),
    [data?.companyId],
  );
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;
  const r = data;
  const curated = r.kind === 'curated';

  if (editing && taxonomy) {
    return (
      <Page>
        <PageHeader
          back={{ to: `/roles/${r.id}`, label: r.title }}
          title="Edit role"
          description={
            curated
              ? 'Facts and your own summary only. Never paste the original description.'
              : 'Changes apply from the next ranking run.'
          }
        />
        {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
        <Card>
          <RoleForm
            taxonomy={taxonomy}
            curated={curated}
            initial={{
              ...r,
              skills: r.skills.map((s) => ({ skillId: s.skillId, required: s.required })),
            }}
            submitLabel="Save changes"
            onCancel={() => setEditing(false)}
            onSubmit={async (input) => {
              try {
                // PUT /roles/:id validates against RoleInput or CuratedRoleInput
                // depending on the existing role's kind, decided server-side —
                // its schema can't be expressed as static RPC input, so this one
                // call goes through fetch directly (URL still built by hc).
                const url = admin.roles[':id'].$url({ param: { id: r.id } });
                const res = await fetch(url, {
                  method: 'PUT',
                  credentials: 'include',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify(input),
                });
                const updated = await unwrap<NonNullable<typeof data>>(
                  res as ClientResponse<{ data: NonNullable<typeof data> }>,
                );
                setData(updated);
                setEditing(false);
                setMsg({ tone: 'info', text: 'Role saved.' });
              } catch (e) {
                setMsg({
                  tone: 'error',
                  text: e instanceof ApiRequestError ? e.message : 'Could not save.',
                });
              }
            }}
          />
        </Card>
      </Page>
    );
  }

  const salary = salaryRange(r.salaryMin, r.salaryMax, r.currency);
  const required = r.skills.filter((s) => s.required);
  const nice = r.skills.filter((s) => !s.required);
  const companyName = curated
    ? (r.sourceCompanyName ?? 'Unknown company')
    : (r.companyName ?? 'Company');
  const facts: [LucideIcon, string][] = [];
  if (r.seniority) facts.push([Layers, label(SENIORITY_LABELS, r.seniority)]);
  if (r.employmentType) facts.push([Briefcase, label(EMPLOYMENT_LABELS, r.employmentType)]);
  facts.push([Globe, r.regions.length ? r.regions.map(regionShortLabel).join(', ') : 'Worldwide']);
  if (r.overlapHours !== null)
    facts.push([Clock, `${r.overlapHours}h overlap${r.timezone ? ` with ${r.timezone}` : ''}`]);
  if (salary) facts.push([Banknote, salary]);
  if (r.industry) facts.push([Building2, r.industry]);
  // Deterministic sanity checks on the role's own fields. Anything that needs
  // reading the summary is left to the curator.
  const warnings: string[] = [];
  if (r.salaryMin && r.salaryMax && r.salaryMax / r.salaryMin >= 4)
    warnings.push(`The salary range (${salary}) is too wide to filter anyone.`);
  if (!curated && required.length === 0)
    warnings.push('No required skills, so the required-skills rule scores nobody.');

  return (
    <Page>
      <PageHeader
        back={{ to: '/roles', label: 'Roles' }}
        title={r.title}
        status={
          <>
            <RoleStatusChip status={r.status} />
            {curated && (
              <Badge variant="external" className="font-normal">
                External listing
              </Badge>
            )}
          </>
        }
        description={
          <>
            {!curated && r.companyId ? (
              <Link to={`/companies/${r.companyId}`} className="text-foreground underline-offset-4">
                {companyName}
              </Link>
            ) : (
              companyName
            )}{' '}
            · Posted {fmtDate(r.createdAt)}
            {owner &&
              billing &&
              ` · ${owner.creditsRemaining} role credit${owner.creditsRemaining === 1 ? '' : 's'} left`}
          </>
        }
        actions={
          <>
            {r.sourceUrl && (
              <a
                className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'no-underline')}
                href={r.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Original posting
                <ArrowUpRight data-icon="inline-end" />
              </a>
            )}
            {canCloseRole(r.status) && (
              <ConfirmButton
                variant="ghost"
                title="Close this role?"
                description={
                  curated
                    ? 'It comes off the public site and out of digests.'
                    : 'Matching stops and no more introductions go out. This cannot be undone.'
                }
                confirmLabel="Close role"
                destructive
                onConfirm={async () => {
                  setData(await unwrap(admin.roles[':id'].close.$post({ param: { id: r.id } })));
                  setMsg({ tone: 'info', text: 'Role closed.' });
                }}
              >
                Close
              </ConfirmButton>
            )}
            {curated && r.status === 'open' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  void unwrap(admin.roles[':id'].verify.$post({ param: { id: r.id } }))
                    .then((updated) => {
                      setData(updated);
                      setMsg({ tone: 'info', text: 'Marked verified today.' });
                    })
                    .catch((e: unknown) => setMsg({ tone: 'error', text: errorMessage(e) }))
                }
              >
                Still live
              </Button>
            )}
            {r.status !== 'closed' && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <Pencil data-icon="inline-start" />
                Edit role
              </Button>
            )}
          </>
        }
      />

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <section className="flex flex-col gap-4 border-t border-border pt-6">
        {r.summary ? (
          <p className="m-0 max-w-prose text-sm leading-6 text-pretty whitespace-pre-line">
            {r.summary}
          </p>
        ) : (
          <None>No summary.</None>
        )}
        <ul className="m-0 flex list-none flex-wrap gap-x-6 gap-y-2 p-0 text-sm">
          {facts.map(([Icon, text]) => (
            <li key={text} className="inline-flex items-center gap-1.5">
              <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
              {text}
            </li>
          ))}
        </ul>
        {(required.length > 0 || nice.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            {required.length > 0 && (
              <>
                <span className="text-muted-foreground">Needs</span>
                <SkillChips names={required.map((s) => s.name)} />
              </>
            )}
            {nice.length > 0 && (
              <>
                <span className="text-muted-foreground">Nice to have</span>
                <SkillChips names={nice.map((s) => s.name)} outline />
              </>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <p className="m-0 flex items-start gap-2 text-sm text-warning-subtle-foreground">
            <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            {warnings.join(' ')} Worth checking with {companyName}.
          </p>
        )}
      </section>

      {r.kind === 'company' && (
        <Candidates
          roleId={r.id}
          roleStatus={r.status}
          roleRegions={r.regions}
          company={companyName}
        />
      )}
    </Page>
  );
}
